import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export function CancelRequestDialog({ req, onClose }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { selectedId } = useProject();
  const [cancelReason, setCancelReason] = useState("");

  useEffect(() => {
    if (req) setCancelReason("");
  }, [req]);

  const cancelReqMut = useMutation({
    mutationFn: async () => api.post(`/file-requests/${req.id}/cancel`, { reason: cancelReason }),
    onSuccess: () => {
      toast.success(t("filesPage.toastCancelled"));
      onClose();
      qc.invalidateQueries({ queryKey: ["file-requests", selectedId] });
      qc.invalidateQueries({ queryKey: ["documents", selectedId] });
      qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cancel-request-dialog">
        <DialogHeader><DialogTitle>{t("filesPage.cancelSubmit")} — {req?.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">{t("filesPage.cancelNote")}</p>
          <div className="space-y-1.5">
            <Label>{t("filesPage.reasonL")}</Label>
            <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder={t("filesPage.cancelPh")} data-testid="cancel-request-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" onClick={() => cancelReqMut.mutate()} disabled={cancelReqMut.isPending} data-testid="cancel-request-submit-button">
            {cancelReqMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("filesPage.cancelSubmit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
